import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export const usePendingInterviews = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['pending-interviews', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      // Find test responses where:
      // 1. User is the candidate
      // 2. Has been invited to interview (interview_invited_at is not null)
      // 3. Token hasn't expired
      // 4. No availability has been submitted yet
      const { data: pendingResponses, error } = await supabase
        .from('test_responses')
        .select(`
          id,
          interview_invited_at,
          availability_token_expires,
          tests!inner (
            job_title,
            title,
            profiles!tests_creator_id_fkey (
              organization
            )
          )
        `)
        .eq('candidate_id', user.id)
        .not('interview_invited_at', 'is', null)
        .gt('availability_token_expires', new Date().toISOString());

      if (error) {
        console.error('Error fetching pending interviews:', error);
        throw error;
      }

      if (!pendingResponses?.length) return [];

      // Check which ones don't have availability submitted yet
      const responseIds = pendingResponses.map(r => r.id);
      
      const { data: existingAvailability, error: availabilityError } = await supabase
        .from('candidate_interview_availability')
        .select('test_response_id')
        .in('test_response_id', responseIds);

      if (availabilityError) {
        console.error('Error checking existing availability:', availabilityError);
        throw availabilityError;
      }

      const submittedResponseIds = new Set(
        existingAvailability?.map(a => a.test_response_id) || []
      );

      // Filter out responses that already have availability submitted
      const pendingInvitations = pendingResponses
        .filter(response => !submittedResponseIds.has(response.id))
        .map(response => ({
          id: response.id,
          job_title: response.tests?.job_title || response.tests?.title || 'Position',
          organization: response.tests?.profiles?.organization || 'Company',
          interview_invited_at: response.interview_invited_at
        }));

      return pendingInvitations;
    },
    enabled: !!user?.id,
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
};